import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import type { WorkRequestItem } from "@pkhoffice/shared";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { fetchWorkRequests } from "@/features/task/api";

type Filters = { q: string; fromDate: string; toDate: string };
const EMPTY: Filters = { q: "", fromDate: "", toDate: "" };

export function WorkRequestsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);

  const { data, isLoading } = useQuery({
    queryKey: ["work-requests", page, pageSize, applied],
    queryFn: () =>
      fetchWorkRequests({
        page,
        pageSize: pageSize,
        q: applied.q || undefined,
        fromDate: applied.fromDate || undefined,
        toDate: applied.toDate || undefined,
      }),
  });

  const columns: Column<WorkRequestItem>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "code",
      header: "Số ký hiệu",
      width: "180px",
      render: (r) => <span className="font-mono text-xs">{r.registerNumber ?? r.code ?? "—"}</span>,
    },
    {
      key: "title",
      header: "Tiêu đề",
      render: (r) => <span className="font-medium">{r.title ?? "—"}</span>,
    },
    {
      key: "creator",
      header: "Người tạo",
      width: "200px",
      render: (r) => <span>{r.creatorName ?? "—"}</span>,
    },
    {
      key: "createDate",
      header: "Ngày tạo",
      width: "130px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.createDate?.slice(0, 10) ?? "—"}</span>
      ),
    },
    {
      key: "signLevel",
      header: "Cấp ký của tôi",
      width: "130px",
      align: "center",
      render: (r) => <span className="tabular-nums">{r.signLevel ?? "—"}</span>,
    },
    {
      key: "myState",
      header: "Trạng thái xử lý",
      width: "140px",
      align: "center",
      render: (r) => <span className="tabular-nums">{r.myState ?? "—"}</span>,
    },
  ];

  const submit = () => {
    setApplied(filters);
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Theo dõi yêu cầu công việc"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} yêu cầu`}
        breadcrumb="Công việc › Yêu cầu"
      />
      <SearchPanel
        fields={[
          {
            label: "Từ khóa",
            control: (
              <input
                className={filterInputClass}
                placeholder="Tiêu đề, số ký hiệu…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              />
            ),
          },
          {
            label: "Từ ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.fromDate}
                onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
              />
            ),
          },
          {
            label: "Đến ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.toDate}
                onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
              />
            ),
          },
        ]}
        onSubmit={submit}
      />
      <DataTable<WorkRequestItem>
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.textId}
        isLoading={isLoading}
        empty={
          <>
            <FileText className="inline h-4 w-4 mr-1" /> Không có yêu cầu nào
          </>
        }
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}
